import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { emailVersandPfad, emailVersandStatusPfad } from '@mcikm-vote/shared';
import type { EmailVersandStatus } from '@mcikm-vote/shared';

import { apiAnfrage } from '../api/anfrage';
import type { ApiFehler } from '../api/anfrage';
import { VERANSTALTUNGEN_SCHLUESSEL } from './veranstaltungenApi';

/**
 * E-Mail-Versand der Zugangskarten (ÄA-7).
 *
 * Läuft im Hintergrund: {@link useEmailVersandStarten} kehrt sofort mit dem Ausgangsstand zurück,
 * {@link useEmailVersandStatus} fragt den Fortschritt ab — per Echtzeit-Anstoß sofort erneuert und
 * währenddessen zusätzlich im Takt, als Rückfall bei blockierten WebSockets (§22.2, §38).
 */

function statusSchluessel(veranstaltungId: string): readonly unknown[] {
  return [...VERANSTALTUNGEN_SCHLUESSEL, veranstaltungId, 'email-versand-status'];
}

/** Abstand der Statusabfrage, solange ein Versand läuft. */
const STATUS_ABSTAND_MS = 2_000;

export function useEmailVersandStatus(
  veranstaltungId: string,
  aktiv: boolean,
): UseQueryResult<EmailVersandStatus, ApiFehler> {
  return useQuery<EmailVersandStatus, ApiFehler>({
    queryKey: statusSchluessel(veranstaltungId),
    queryFn: () => apiAnfrage<EmailVersandStatus>(emailVersandStatusPfad(veranstaltungId)),
    enabled: aktiv && veranstaltungId !== '',
    refetchInterval: (abfrage) =>
      abfrage.state.data?.laufend === true ? STATUS_ABSTAND_MS : false,
  });
}

export function useEmailVersandStarten(
  veranstaltungId: string,
): UseMutationResult<EmailVersandStatus, ApiFehler, string[]> {
  const queryClient = useQueryClient();

  return useMutation<EmailVersandStatus, ApiFehler, string[]>({
    mutationFn: (empfaenger) =>
      apiAnfrage<EmailVersandStatus>(emailVersandPfad(veranstaltungId), {
        methode: 'POST',
        koerper: { empfaenger },
      }),
    onSuccess: (status) => {
      queryClient.setQueryData(statusSchluessel(veranstaltungId), status);
    },
  });
}
